import React, { useState, Children } from 'react';
import { motion } from 'framer-motion';
import { PageHeader } from '../components/PageHeader';
import { MapPinIcon, PhoneIcon, MailIcon, ClockIcon, SendIcon, ArrowRightIcon, CheckCircle2Icon, UsersIcon } from 'lucide-react';
const fadeUpVariants = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut'
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
export function ContactPage() {
  const [formState, setFormState] = useState({
    name: '',
    email: '',
    phone: '',
    subject: 'General Inquiry',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    // Simulate submission
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSubmitted(true);
      setFormState({
        name: '',
        email: '',
        phone: '',
        subject: 'General Inquiry',
        message: ''
      });
      setTimeout(() => setIsSubmitted(false), 5000);
    }, 1500);
  };
  return <div className="bg-gesl-offWhite" data-id="element-389">
      <PageHeader title="Contact Us" subtitle="Partner with us for scientifically sound, innovative, and sustainable environmental solutions." data-id="element-390" />

      {/* Quick Contact Cards - Overlapping Header */}
      <section className="relative z-20 -mt-16 mb-0" data-id="element-391">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-392">
          <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="grid md:grid-cols-3 gap-6" data-id="element-393">
            {[{
            icon: PhoneIcon,
            label: 'Phone',
            value: '+234 803 461 2933',
            link: 'tel:+2348034612933'
          }, {
            icon: MailIcon,
            label: 'Email',
            value: 'info@greenwater.ng',
            link: 'mailto:info@greenwater.ng'
          }, {
            icon: MapPinIcon,
            label: 'Office',
            value: 'Jabi, Abuja, Nigeria',
            link: '#map'
          }].map((card, idx) => <motion.a key={idx} href={card.link} variants={fadeUpVariants} className="bg-white p-8 rounded-sm shadow-xl border border-gray-100 flex flex-col items-center text-center group hover:-translate-y-1 transition-transform duration-300" data-id="element-394">
                <div className="h-14 w-14 rounded-full bg-gesl-gold/10 flex items-center justify-center mb-5 group-hover:bg-gesl-gold transition-colors duration-300" data-id="element-395">
                  <card.icon className="h-6 w-6 text-gesl-gold group-hover:text-white transition-colors duration-300" data-id="element-396" />
                </div>
                <h3 className="text-gesl-dark font-bold mb-2 text-lg" data-id="element-397">
                  {card.label}
                </h3>
                <p className="text-gray-600 font-medium" data-id="element-398">{card.value}</p>
              </motion.a>)}
          </motion.div>
        </div>
      </section>

      {/* Why Contact Us Strip */}
      <section className="py-20 bg-white border-b border-gray-100" data-id="element-399">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-400">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-50px'
        }} variants={staggerContainer} className="flex flex-col md:flex-row justify-center items-center gap-8 md:gap-0 md:divide-x divide-gray-200" data-id="element-401">
            {[{
            icon: ClockIcon,
            text: '24-Hour Response'
          }, {
            icon: UsersIcon,
            text: 'Expert Consultation'
          }, {
            icon: MapPinIcon,
            text: 'Nationwide Coverage'
          }].map((benefit, idx) => <motion.div key={idx} variants={fadeUpVariants} className="flex items-center gap-4 px-8 md:px-12" data-id="element-402">
                <div className="h-12 w-12 rounded-full bg-gesl-offWhite flex items-center justify-center flex-shrink-0" data-id="element-403">
                  <benefit.icon className="h-5 w-5 text-gesl-gold" data-id="element-404" />
                </div>
                <span className="text-gesl-dark font-bold tracking-wide" data-id="element-405">
                  {benefit.text}
                </span>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* Main Contact Section */}
      <section className="py-24 lg:py-32 bg-gesl-offWhite" data-id="element-406">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-407">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-8 items-start" data-id="element-408">
            {/* Left Column: Form */}
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-100px'
          }} variants={fadeUpVariants} className="lg:col-span-7 bg-white p-8 md:p-12 rounded-sm shadow-sm border border-gray-100" data-id="element-409">
              <div className="flex items-center gap-3 mb-4" data-id="element-410">
                <div className="h-px w-8 bg-gesl-gold" data-id="element-411" />
                <span className="text-gesl-gold font-bold tracking-wider uppercase text-xs" data-id="element-412">
                  Get In Touch
                </span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gesl-dark mb-4 tracking-tight" data-id="element-413">
                Send us a Message
              </h2>
              <p className="text-gray-600 mb-10 leading-relaxed" data-id="element-414">
                Fill out the form below and our team will get back to you within
                24 hours to discuss your environmental project needs.
              </p>

              {isSubmitted && <motion.div initial={{
              opacity: 0,
              y: -10
            }} animate={{
              opacity: 1,
              y: 0
            }} className="mb-8 p-5 bg-gesl-green/10 border border-gesl-green/20 rounded-sm flex items-start gap-4" data-id="element-415">
                  <CheckCircle2Icon className="h-6 w-6 text-gesl-green flex-shrink-0 mt-0.5" data-id="element-416" />
                  <div data-id="element-417">
                    <h4 className="text-gesl-dark font-bold mb-1" data-id="element-418">
                      Message Sent Successfully
                    </h4>
                    <p className="text-gesl-charcoal/80 text-sm" data-id="element-419">
                      Thank you for your inquiry. A representative will contact
                      you shortly.
                    </p>
                  </div>
                </motion.div>}

              <form onSubmit={handleSubmit} className="space-y-6" data-id="element-420">
                <div className="grid md:grid-cols-2 gap-6" data-id="element-421">
                  <div data-id="element-422">
                    <label htmlFor="name" className="block text-sm font-bold text-gesl-charcoal mb-2" data-id="element-423">
                      Full Name *
                    </label>
                    <input type="text" id="name" required value={formState.name} onChange={e => setFormState({
                    ...formState,
                    name: e.target.value
                  })} className="w-full px-4 py-3.5 rounded-sm border border-gray-200 focus:border-gesl-green focus:ring-1 focus:ring-gesl-green outline-none transition-colors bg-gesl-offWhite focus:bg-white" placeholder="John Doe" data-id="element-424" />
                  </div>
                  <div data-id="element-425">
                    <label htmlFor="email" className="block text-sm font-bold text-gesl-charcoal mb-2" data-id="element-426">
                      Email Address *
                    </label>
                    <input type="email" id="email" required value={formState.email} onChange={e => setFormState({
                    ...formState,
                    email: e.target.value
                  })} className="w-full px-4 py-3.5 rounded-sm border border-gray-200 focus:border-gesl-green focus:ring-1 focus:ring-gesl-green outline-none transition-colors bg-gesl-offWhite focus:bg-white" placeholder="john@example.com" data-id="element-427" />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6" data-id="element-428">
                  <div data-id="element-429">
                    <label htmlFor="phone" className="block text-sm font-bold text-gesl-charcoal mb-2" data-id="element-430">
                      Phone Number
                    </label>
                    <input type="tel" id="phone" value={formState.phone} onChange={e => setFormState({
                    ...formState,
                    phone: e.target.value
                  })} className="w-full px-4 py-3.5 rounded-sm border border-gray-200 focus:border-gesl-green focus:ring-1 focus:ring-gesl-green outline-none transition-colors bg-gesl-offWhite focus:bg-white" placeholder="+234 800 000 0000" data-id="element-431" />
                  </div>
                  <div data-id="element-432">
                    <label htmlFor="subject" className="block text-sm font-bold text-gesl-charcoal mb-2" data-id="element-433">
                      Subject *
                    </label>
                    <select id="subject" value={formState.subject} onChange={e => setFormState({
                    ...formState,
                    subject: e.target.value
                  })} className="w-full px-4 py-3.5 rounded-sm border border-gray-200 focus:border-gesl-green focus:ring-1 focus:ring-gesl-green outline-none transition-colors bg-gesl-offWhite focus:bg-white appearance-none" data-id="element-434">
                      <option data-id="element-435">General Inquiry</option>
                      <option data-id="element-436">EIA Request</option>
                      <option data-id="element-437">Consultation</option>
                      <option data-id="element-438">Partnership</option>
                    </select>
                  </div>
                </div>

                <div data-id="element-439">
                  <label htmlFor="message" className="block text-sm font-bold text-gesl-charcoal mb-2" data-id="element-440">
                    Message *
                  </label>
                  <textarea id="message" required rows={6} value={formState.message} onChange={e => setFormState({
                  ...formState,
                  message: e.target.value
                })} className="w-full px-4 py-3.5 rounded-sm border border-gray-200 focus:border-gesl-green focus:ring-1 focus:ring-gesl-green outline-none transition-colors bg-gesl-offWhite focus:bg-white resize-none" placeholder="How can we help you?" data-id="element-441" />
                </div>

                <button type="submit" disabled={isSubmitting} className="flex items-center justify-center gap-2 w-full sm:w-auto px-10 py-4 bg-gesl-gold text-white font-bold rounded-sm hover:bg-gesl-dark transition-colors duration-300 disabled:opacity-70 disabled:cursor-not-allowed shadow-md" data-id="element-442">
                  {isSubmitting ? <>
                      <span className="h-5 w-5 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-443" />
                      Sending...
                    </> : <>
                      Send Message
                      <SendIcon className="h-4 w-4 ml-1" data-id="element-444" />
                    </>}
                </button>
              </form>
            </motion.div>

            {/* Right Column: Contact Info Panel */}
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-100px'
          }} variants={fadeUpVariants} className="lg:col-span-5 bg-gesl-dark text-white p-10 md:p-12 rounded-sm shadow-2xl relative overflow-hidden" data-id="element-445">
              {/* Decorative background element */}
              <div className="absolute -bottom-24 -right-24 w-64 h-64 bg-gesl-green rounded-full blur-3xl opacity-20" data-id="element-446" />

              <h3 className="text-2xl font-bold mb-10 text-gesl-gold tracking-tight" data-id="element-447">
                Head Office
              </h3>

              <div className="space-y-8 relative z-10" data-id="element-448">
                <div className="flex items-start group" data-id="element-449">
                  <div className="h-11 w-11 rounded-full bg-white/5 flex items-center justify-center mr-5 flex-shrink-0 group-hover:bg-gesl-gold transition-colors duration-300" data-id="element-450">
                    <MapPinIcon className="h-5 w-5 text-gesl-gold group-hover:text-white transition-colors duration-300" data-id="element-451" />
                  </div>
                  <div data-id="element-452">
                    <h4 className="text-xs font-bold text-white/40 uppercase tracking-widest mb-2" data-id="element-453">
                      Address
                    </h4>
                    <p className="text-white/90 leading-relaxed font-medium" data-id="element-454">
                      Suite 3, 23 IT Igbani Street
                      <br data-id="element-455" />
                      Jabi, Abuja
                      <br data-id="element-456" />
                      Nigeria
                    </p>
                  </div>
                </div>

                <div className="flex items-start group" data-id="element-457">
                  <div className="h-11 w-11 rounded-full bg-white/5 flex items-center justify-center mr-5 flex-shrink-0 group-hover:bg-gesl-gold transition-colors duration-300" data-id="element-458">
                    <PhoneIcon className="h-5 w-5 text-gesl-gold group-hover:text-white transition-colors duration-300" data-id="element-459" />
                  </div>
                  <div data-id="element-460">
                    <h4 className="text-xs font-bold text-white/40 uppercase tracking-widest mb-2" data-id="element-461">
                      Phone
                    </h4>
                    <a href="tel:+2348034612933" className="text-white/90 font-medium hover:text-gesl-gold transition-colors block" data-id="element-462">
                      +234 803 461 2933
                    </a>
                  </div>
                </div>

                <div className="flex items-start group" data-id="element-463">
                  <div className="h-11 w-11 rounded-full bg-white/5 flex items-center justify-center mr-5 flex-shrink-0 group-hover:bg-gesl-gold transition-colors duration-300" data-id="element-464">
                    <MailIcon className="h-5 w-5 text-gesl-gold group-hover:text-white transition-colors duration-300" data-id="element-465" />
                  </div>
                  <div data-id="element-466">
                    <h4 className="text-xs font-bold text-white/40 uppercase tracking-widest mb-2" data-id="element-467">
                      Email
                    </h4>
                    <div className="space-y-2" data-id="element-468">
                      <a href="mailto:ceo@greenwater.ng" className="text-white/90 font-medium hover:text-gesl-gold transition-colors block break-all" data-id="element-469">
                        ceo@greenwater.ng
                      </a>
                      <a href="mailto:info@greenwater.ng" className="text-white/90 font-medium hover:text-gesl-gold transition-colors block break-all" data-id="element-470">
                        info@greenwater.ng
                      </a>
                      <a href="mailto:greenwaterenvironmentalservice@gmail.com" className="text-white/90 font-medium hover:text-gesl-gold transition-colors block break-all text-sm" data-id="element-471">
                        greenwaterenvironmentalservice@gmail.com
                      </a>
                    </div>
                  </div>
                </div>

                <div className="flex items-start group" data-id="element-472">
                  <div className="h-11 w-11 rounded-full bg-white/5 flex items-center justify-center mr-5 flex-shrink-0 group-hover:bg-gesl-gold transition-colors duration-300" data-id="element-473">
                    <ClockIcon className="h-5 w-5 text-gesl-gold group-hover:text-white transition-colors duration-300" data-id="element-474" />
                  </div>
                  <div data-id="element-475">
                    <h4 className="text-xs font-bold text-white/40 uppercase tracking-widest mb-2" data-id="element-476">
                      Business Hours
                    </h4>
                    <p className="text-white/90 font-medium" data-id="element-477">
                      Mon - Fri: 8:00 AM - 5:00 PM
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-12 pt-8 border-t border-white/10 relative z-10" data-id="element-478">
                <h4 className="text-xs font-bold text-white/40 uppercase tracking-widest mb-4" data-id="element-479">
                  Follow Us
                </h4>
                <div className="flex gap-4" data-id="element-480">
                  <a href="#" className="h-10 w-10 rounded-full bg-white/5 flex items-center justify-center hover:bg-gesl-gold transition-colors text-white" data-id="element-481">
                    <span className="font-bold text-sm" data-id="element-482">in</span>
                  </a>
                  <a href="#" className="h-10 w-10 rounded-full bg-white/5 flex items-center justify-center hover:bg-gesl-gold transition-colors text-white" data-id="element-483">
                    <span className="font-bold text-sm" data-id="element-484">X</span>
                  </a>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section id="map" className="w-full h-96 bg-gesl-dark relative overflow-hidden flex items-center justify-center" data-id="element-485">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCI+CjxyZWN0IHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgZmlsbD0ibm9uZSIvPgo8cGF0aCBkPSJNMCAwaDQwdjQwaC00MHoiIGZpbGw9Im5vbmUiIHN0cm9rZT0icmdiYSgyNTUsMjU1LDI1NSwwLjA1KSIgc3Ryb2tlLXdpZHRoPSIxIi8+Cjwvc3ZnPg==')] opacity-30" data-id="element-486" />
        <div className="absolute inset-0 bg-gradient-to-t from-gesl-dark to-transparent opacity-80" data-id="element-487" />

        <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="relative z-10 flex flex-col items-center text-center p-8 bg-gesl-dark/80 backdrop-blur-md border border-white/10 rounded-sm shadow-2xl max-w-sm mx-4" data-id="element-488">
          <div className="h-16 w-16 bg-gesl-gold/20 rounded-full flex items-center justify-center mb-5 animate-pulse" data-id="element-489">
            <MapPinIcon className="h-8 w-8 text-gesl-gold" data-id="element-490" />
          </div>
          <h3 className="text-xl font-bold text-white mb-2" data-id="element-491">
            Greenwater Environmental
          </h3>
          <p className="text-white/70 font-medium" data-id="element-492">
            Suite 3, 23 IT Igbani Street
            <br data-id="element-493" />
            Jabi, Abuja, Nigeria
          </p>
        </motion.div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-gesl-green to-gesl-dark relative overflow-hidden" data-id="element-494">
        <div className="absolute top-0 right-0 w-64 h-64 bg-gesl-gold/10 rounded-full blur-3xl" data-id="element-495" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/5 rounded-full blur-3xl" data-id="element-496" />

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10" data-id="element-497">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeUpVariants} data-id="element-498">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6 tracking-tight" data-id="element-499">
              Ready to Start Your Project?
            </h2>
            <p className="text-lg md:text-xl text-white/80 mb-10 max-w-2xl mx-auto font-light" data-id="element-500">
              Get in touch for a consultation on your next environmental
              project. Our team of experts is ready to deliver sustainable
              solutions.
            </p>
            <button onClick={() => window.scrollTo({
            top: 0,
            behavior: 'smooth'
          })} className="inline-flex items-center gap-2 px-8 py-4 bg-gesl-gold text-white font-bold rounded-sm hover:bg-white hover:text-gesl-dark transition-colors duration-300 shadow-lg group" data-id="element-501">
              Contact Us Today
              <ArrowRightIcon className="h-5 w-5 group-hover:translate-x-1 transition-transform" data-id="element-502" />
            </button>
          </motion.div>
        </div>
      </section>
    </div>;
}